import React, { useMemo, useState, useEffect, useCallback } from "react";
import ReusableTable from "../components/Table/ReusableTable";
import {
  Search,
  Download, Eye, Check, XCircle,
  Zap,
  TrendingUp, Loader2
} from "lucide-react";
import Button from "../components/Button/Button";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { post } from "../utils/api";
import CustomDialog from "../components/dialog/CustomDialog";

const KpiCard = ({ title, value, icon: Icon, trend, color = "indigo" }) => (
  <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all group animate-in fade-in slide-in-from-bottom-3 duration-500">
    <div className="flex justify-between items-start mb-4">
      <div className={`p-3 rounded-xl bg-${color}-50 text-${color}-500 group-hover:scale-110 transition-transform`}>
        <Icon size={20} />
      </div>
      {trend && (
        <span className="flex items-center gap-1 text-[10px] font-bold text-emerald-500 bg-emerald-50 px-2 py-0.5 rounded-full">
          <TrendingUp size={10} className="mr-0.5" /> {trend}
        </span>
      )}
    </div>
    <div>
      <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-1">{title}</p>
      <h3 className="text-2xl font-black text-gray-800 tracking-tight">{value}</h3>
    </div>
  </div>
);

const DistributorListing = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [selectedSeqNum, setSelectedSeqNum] = useState(null);
  const [globalSearch, setGlobalSearch] = useState("");

  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
  const [selectedDistributorForApproval, setSelectedDistributorForApproval] =
    useState(null);
  const [viewedAgreements, setViewedAgreements] = useState({});

  // E-sign state
  const [isEsignModalOpen, setIsEsignModalOpen] = useState(false);
  const [esignName, setEsignName] = useState("");
  const [esignLoading, setEsignLoading] = useState(false);
  const [selectedDistributorForEsign, setSelectedDistributorForEsign] =
    useState(null);
  const [esignStep, setEsignStep] = useState("init"); // "init" | "verify"

  // Commercial details state
  const [commercialDialogVisible, setCommercialDialogVisible] = useState(false);
  const [commercialLoading, setCommercialLoading] = useState(false);
  const [selectedDistributorForCommercials, setSelectedDistributorForCommercials] = useState(null);
  const [commercialDetails, setCommercialDetails] = useState([]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const userDetails = JSON.parse(localStorage.getItem("user_details"));
      const userEmail = userDetails?.email;

      if (!userEmail) {
        toast.error("User email not found. Please log in again.");
        setLoading(false);
        return;
      }

      const response = await post("/onboarding/fetchDistributorListing", {
        distributor_email: userEmail,
      });

      if (response.data?.Success) {
        const mappedData = response.data.Data.map((item) => {
          const uniqueFunds = item.products
            ? [
              ...new Set(
                item.products.map((p) => p.fund_name).filter(Boolean),
              ),
            ]
            : [];
          const uniqueClasses = item.products
            ? [
              ...new Set(
                item.products.map((p) => p.class_code).filter(Boolean),
              ),
            ]
            : [];

          return {
            distributor_name: item.distributor_name,
            arn_code: item.arn_code,
            pan: item.pan_number,
            distributor_sequence_number: item.distributor_sequence_number,
            distributor_status: item.distributor_status, // "Approved", "Rejected", "Pending"
            sales_head_status: item.sales_head_status, // "Approved", "Pending", etc.
            agreement_status: item.agreement_status, // "Approved by Sales Head", etc.
            fundName: uniqueFunds.join(", ") || "-",
            class: uniqueClasses.join(", ") || "-",
            products: item.products || [],
            id: item.id, // Ensure ID is available if needed for esign API
          };
        });

        // Bring "Pending" records to the top, keep everything else in its existing order
        mappedData.sort((a, b) => {
          const aIsPending = (a.distributor_status || "").toLowerCase().includes("pending") ? 0 : 1;
          const bIsPending = (b.distributor_status || "").toLowerCase().includes("pending") ? 0 : 1;
          return aIsPending - bIsPending;
        });

        setData(mappedData);
      } else {
        if (response.data?.Message !== "No Records Found") {
          toast.error(response.data?.Message || "Failed to fetch data");
        }
        setData([]);
      }
    } catch (error) {
      console.error("Fetch error:", error);
      toast.error("Error fetching distributor listing");
    } finally {
      setLoading(false);
    }
  };

  const stats = useMemo(() => ({
    total: data.length,
    pending: data.filter(d => (d.distributor_status || "").toLowerCase().includes("pending")).length,
    approved: data.filter(d => (d.distributor_status || "").toLowerCase().includes("approved")).length
  }), [data]);

  useEffect(() => {
    fetchData();
  }, []);

  const handleApprove = (row) => {
    setSelectedDistributorForApproval(row);
    setIsApproveModalOpen(true);
  };

  const confirmApprove = async () => {
    if (!selectedDistributorForApproval) return;

    try {
      const response = await post("/onboarding/ApproveRejectDistributor", {
        distributor_sequence_number:
          selectedDistributorForApproval.distributor_sequence_number,
        status: "Approved",
        reason: null,
      });

      if (response.data?.Success) {
        toast.success("Distributor approved successfully");
        setIsApproveModalOpen(false);
        fetchData();
      } else {
        toast.error(response.data?.Message || "Approval failed");
      }
    } catch (error) {
      console.error("Approval error:", error);
      toast.error("Error approving distributor");
    }
  };

  const handleRejectInit = (row) => {
    setSelectedSeqNum(row.distributor_sequence_number);
    setIsRejectModalOpen(true);
    setRejectionReason("");
  };

  const handleRejectSubmit = async () => {
    if (!selectedSeqNum) return;
    if (!rejectionReason.trim()) {
      toast.warn("Please enter a rejection reason");
      return;
    }

    try {
      const response = await post("/onboarding/ApproveRejectDistributor", {
        distributor_sequence_number: selectedSeqNum,
        status: "Rejected",
        reason: rejectionReason,
      });

      if (response.data?.Success) {
        toast.success("Distributor rejected successfully");
        setIsRejectModalOpen(false);
        fetchData();
      } else {
        toast.error(response.data?.Message || "Rejection failed");
      }
    } catch (error) { 
      console.error("Rejection error:", error);
      toast.error("Error rejecting distributor");
    }
  };

  const handleDownload = async (row) => {
    try {
      const salesHeadStatus = (row.sales_head_status || "").trim();
      const agreementStatus = (row.agreement_status || "").trim();
      const distStatus = (row.distributor_status || "").trim().toLowerCase();

      const isApprovedBySalesHead =
        salesHeadStatus === "Approved" ||
        salesHeadStatus.includes("Approved") ||
        agreementStatus === "Approved by Sales Head" ||
        agreementStatus.includes("Approved") 

      if (!isApprovedBySalesHead) {
        toast.warning("Agreement status must be Approved by Sales Head to view agreement.");
        return;
      }

      if (!row.distributor_sequence_number) {
        toast.error("Distributor Sequence Number missing for this entry.");
        return;
      }

      toast.info("Generating and downloading agreement...");

      const response = await post(
        "/fileGeneration/downloadGeneratedAgreement",
        { distributor_sequence_number: row.distributor_sequence_number },
      );

      if (response.data && response.data.Success && response.data.Data) {
        setViewedAgreements((prev) => ({
          ...prev,
          [row.distributor_sequence_number]: true,
        }));

        const byteCharacters = atob(response.data.Data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: "application/pdf" });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `Agreement_${row.distributor_name || "Document"}.pdf`,
        );
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        window.URL.revokeObjectURL(url);

        toast.success("Download started successfully");
      } else {
        console.error("Download response error:", response.data);
        toast.error(response.data?.Message || "Failed to generate agreement");
      }
    } catch (error) {
      console.error("Download failed:", error);
      toast.error(
        error.response?.data?.Message || "Failed to download agreement",
      );
    }
  };

  const handleEsignInit = (row) => {
    setSelectedDistributorForEsign(row);
    setEsignName("");
    setEsignStep("init");
    setIsEsignModalOpen(true);
  };

const downloadPdf = async (row) => {
    try {
      const distributor_id = row.distributor_sequence_number;
      if (!distributor_id) {
        toast.error("Distributor Sequence Number missing.");
        return;
      }
 
      console.log("Fetching contract details for:", distributor_id);
      const response = await post("/eSign/getDistributorContractDetails", {
        distributor_id: distributor_id,
      });
 
      if (response.data?.Success && response.data.Data) {

        const contractId = response.data.Data.amc_contractId === null ? response.data.Data.distributor_contractId : response.data.Data.amc_contractId;
 
        if (!contractId) {
          alert("Contract ID missing. Please complete e-sign first.");
          return;
        }
 
        const url = `https://d14wm9j0bgcvlw.cloudfront.net/DistributorInvoiceFiles/Esign/signed_${contractId}.pdf`;
 
        const link = document.createElement("a");
        link.href = url;
        link.download = `signed_${contractId}.pdf`;
        link.click();
      } else {
        alert(response.data?.Message || "Contract ID missing. Please complete e-sign first.");
      }
    } catch (error) {
      console.error("Error downloading PDF:", error);
      toast.error("Error occurred while fetching contract details.");
    }
  };

  const handleEnableLocationAccess = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        toast.error("Geolocation is not supported by your browser.");
        reject(new Error("Geolocation not supported"));
        return;
      }

      toast.info("Requesting browser location permission for E-signing...");

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve(position);
        },
        (error) => {
          console.warn("Location access error:", error);
          toast.error("Location access is required for E-signing. Please allow location in browser site settings and try again.");
          reject(error);
        },
        { timeout: 10000 }
      );
    });
  };

  const handleEsignSubmit = async () => {
    if (!selectedDistributorForEsign) return;
    if (!esignName.trim()) {
      toast.warn("Please enter the name for 'In the presence of'");
      return;
    }

    try {
      setEsignLoading(true);

      // Request browser location permission first
      try {
        await handleEnableLocationAccess();
      } catch (locErr) {
        setEsignLoading(false);
        return;
      }

      // Payload for INITIAL contract (Distributor flow)
      const payload = {
        name_1: esignName.trim(),
        distributor_id:
          selectedDistributorForEsign.id ||
          selectedDistributorForEsign.distributor_sequence_number, // Fallback
        witness_name: esignName.trim(),
      };

      const signzyResp = await post("/eSign/initiate-signzy-contract", payload);

      if (!signzyResp.data?.Success) {
        toast.error(
          signzyResp.data?.Message || "Failed to initiate Signzy contract",
        );
        setEsignLoading(false);
        return;
      }

      const data = signzyResp.data.Data;
      console.log(data, "signzy response data");

      const contractId = data?.contractId;
      const signerUrl = data?.signerdetail?.[0]?.workflowUrl;

      if (!contractId) {
        toast.error("Contract ID missing from Signzy response!");
        setEsignLoading(false);
        return;
      }

      if (!signerUrl) {
        toast.error("Signer workflow URL missing!");
        setEsignLoading(false);
        return;
      }

      // Save to LOCAL STORAGE as per reference
      localStorage.setItem("signzy_contractId", contractId);

      // Open Signzy workflow
      const win = window.open(signerUrl, "_blank");
      if (!win) {
        toast.error("Pop-up blocked! Please allow pop-ups for this site to open E-sign window.");
      }

      setEsignStep("verify");
      toast.info(
        "E-sign initiated. Please complete the document in the new tab.",
      );
    } catch (error) {
      console.error("E-sign error:", error);
      toast.error("E-sign failed");
    } finally {
      setEsignLoading(false);
    }
  };

  const handleEsignVerify = async () => {
    try {
      setEsignLoading(true);
      const esignResp = await post("/onboarding/esign/complete", {
        distributor_id:
          selectedDistributorForEsign.id ||
          selectedDistributorForEsign.distributor_sequence_number,
      });

      if (esignResp.data?.Success) {
        toast.success(esignResp.data.Message);
        setIsEsignModalOpen(false);
        fetchData();
      } else {
        toast.warn(esignResp.data?.Message || "Failed to update esign status");
      }
    } catch (error) {
      console.error("E-sign verify error:", error);
      toast.error("Failed to verify e-sign status");
    } finally {
      setEsignLoading(false);
    }
  };

  const openCommercialDetails = useCallback(async (row) => {
    setSelectedDistributorForCommercials(row);
    setCommercialDialogVisible(true);
    setCommercialLoading(true);

    try {
      const response = await post(
        "/distributorproductmapping/fetchDistributorCommercialDetails",
        {
          distributor_sequence_number: row.distributor_sequence_number,
        }
      );

      if (response.data?.Success) {
        setCommercialDetails(response.data.Data || []);
      } else {
        setCommercialDetails([]);
      }
    } catch (error) {
      console.error("Error fetching distributor commercial details:", error);
      setCommercialDetails([]);
    } finally {
      setCommercialLoading(false);
    }
  }, []);

  const isProduct2 = useMemo(
    () =>
      commercialDetails.length > 0 &&
      (Number(commercialDetails[0]?.fund_product_id) === 2 ||
        commercialDetails.some(
          (row) =>
            row.management_fee_sharing_per_morethan15cr !== null &&
            row.management_fee_sharing_per_morethan15cr !== undefined &&
            row.management_fee_sharing_per_morethan15cr !== ""
        )),
    [commercialDetails]
  );

  const commercialTableData = useMemo(() => {
    if (!isProduct2) return commercialDetails;

    const result = [];
    commercialDetails.forEach((row) => {
      const hasLess =
        row.management_fee_sharing_per !== null &&
        row.management_fee_sharing_per !== undefined &&
        row.management_fee_sharing_per !== "";
      const hasMore =
        row.management_fee_sharing_per_morethan15cr !== null &&
        row.management_fee_sharing_per_morethan15cr !== undefined &&
        row.management_fee_sharing_per_morethan15cr !== "";

      if (hasLess) {
        result.push({
          ...row,
          _capital_commitment: "Less than 15 Cr",
          _mgmt_fee: row.management_fee_sharing_per,
        });
      }
      if (hasMore) {
        result.push({
          ...row,
          _capital_commitment: "More than 15 Cr",
          _mgmt_fee: row.management_fee_sharing_per_morethan15cr,
        });
      }
    });

    return result.length > 0 ? result : commercialDetails;
  }, [commercialDetails, isProduct2]);

  const commercialColumns = useMemo(() => {
    if (isProduct2) {
      return [
        { Header: "FUND CODE", accessor: "fund_code" },
        { Header: "CLASS CODE", accessor: "class_code" },
        { Header: "CAPITAL COMMITMENT", accessor: "_capital_commitment" },
        {
          Header: "MANAGEMENT FEES P.A.",
          accessor: "_mgmt_fee",
          Cell: ({ value }) => (value !== null && value !== undefined && value !== "" ? `${value}%` : "-"),
        },
        {
          Header: "DISTRIBUTOR SHARING IN MONTHLY FEES",
          accessor: "performance_fee_sharing_per",
          Cell: ({ value }) => (value !== null && value !== undefined && value !== "" ? `${value}%` : "-"),
        },
      ];
    }

    return [
      { Header: "FUND CODE", accessor: "fund_code" },
      { Header: "CLASS CODE", accessor: "class_code" },
      { Header: "CONTRIBUTION (IN CR)", accessor: "size" },
      { Header: "M. FEES", accessor: "management_fee_sharing_per", Cell: ({ value }) => (value !== null && value !== undefined && value !== "" ? `${value}%` : "-") },
      { Header: "TOTAL DISTRIBUTION FEES", accessor: "placement_fee_per", Cell: ({ value }) => (value !== null && value !== undefined && value !== "" ? `${value}%` : "-") },
      { Header: "UPFRONT", accessor: "upfront_fee_per", Cell: ({ value }) => (value !== null && value !== undefined && value !== "" ? `${value}%` : "-") },
      { Header: "TRAIL", accessor: "trail_fee_per", Cell: ({ value }) => (value !== null && value !== undefined && value !== "" ? `${value}%` : "-") },
      {
        Header: "TOTAL FEE",
        accessor: "total_fee_per",
        Cell: ({ row }) => {
          const value = row.original.placement_fee_per;
          return value !== null && value !== undefined && value !== "" ? `${value}%` : "-";
        },
      },
    ];
  }, [isProduct2]);

  const columns = useMemo(
    () => [
      // {
      //   Header: "Distributor Name",
      //   accessor: "distributor_name",
      //   size: 150,
      //   Cell: ({ value }) => (
      //     <div className="font-medium text-gray-900">{value || "-"}</div>
      //   ),
      // },
      {
        Header: "Fund Name",
        accessor: "fundName",
        size: 120,
        Cell: ({ value }) => (
          <div className="truncate" title={value}>
            {value}
          </div>
        ),
      },
      {
        Header: "Class",
        accessor: "class",
        size: 120,
        Cell: ({ value }) => (
          <div className="truncate" title={value}>
            {value}
          </div>
        ),
      },
      {
        Header: "Status",
        accessor: "distributor_status",
        size: 120,
        Cell: ({ value }) => {
          let styles = "bg-gray-50 text-gray-500 border-gray-100";
          const status = (value || "Pending").toLowerCase();

          if (status.includes("approved")) styles = "bg-emerald-50 text-emerald-700 border-emerald-100";
          else if (status.includes("rejected")) styles = "bg-rose-50 text-rose-700 border-rose-100";
          else if (status.includes("pending")) styles = "bg-amber-50 text-amber-700 border-amber-100";

          return (
            <div className={`px-4 py-1.5 rounded-full text-xs font-semibold border inline-flex items-center ${styles}`}>
              {value || "Pending"}
            </div>
          );
        },
      },
      {
        Header: "ACTION",
        accessor: "action",
        size: 580,
        Cell: ({ row }) => {
          const status = row.original.distributor_status || "Pending";
          const salesHeadStatus = (row.original.sales_head_status || "").trim().toLowerCase();
          const agreementStatus = (row.original.agreement_status || "").trim();
          const distStatus = status.trim().toLowerCase();

          const isApprovedBySalesHead =
            salesHeadStatus === "approved" ||
            salesHeadStatus.includes("approved") ||
            agreementStatus === "Approved by Sales Head" ||
            agreementStatus.includes("Approved") 
       
          const normalizedStatus = status.trim().toLowerCase();
          const hasViewedAgreement = Boolean(
            viewedAgreements[row.original.distributor_sequence_number]
          );
          const isApprovedOrRejected =
            normalizedStatus === "approved" ||
            normalizedStatus.includes("approved");
          const isEsignCompleted = distStatus.includes("e-sign");

          return (
            <div className="flex flex-wrap items-center gap-2 min-w-[520px] py-1">
              <button
                onClick={() => openCommercialDetails(row.original)}
                className="inline-flex items-center gap-1.5 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 text-[11px] font-semibold text-indigo-700 shadow-sm transition-all hover:bg-indigo-100"
                title="View Commercials"
              >
                <Eye size={14} />
                View Commercials
              </button>
              <button
                onClick={() => handleDownload(row.original)}
                disabled={!isApprovedBySalesHead}
                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-[11px] font-semibold text-slate-700 shadow-sm transition-all hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-100 disabled:text-slate-400"
                title={isApprovedBySalesHead ? "View Agreement" : "Agreement status must be Approved by Sales Head"}
              >
                <Eye size={14} />
                View Agreement
              </button>
              <button
                onClick={() => handleApprove(row.original)}
                disabled={!hasViewedAgreement}
                className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-[11px] font-semibold text-emerald-700 transition-all hover:bg-emerald-100 disabled:cursor-not-allowed disabled:border-emerald-100 disabled:bg-emerald-50/40 disabled:text-emerald-300"
                title="Approve"
              >
                <Check size={14} />
                Approve
              </button>
              <button
                onClick={() => handleRejectInit(row.original)}
                disabled={!hasViewedAgreement}
                className="inline-flex items-center gap-1.5 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[11px] font-semibold text-rose-700 transition-all hover:bg-rose-100 disabled:cursor-not-allowed disabled:border-rose-100 disabled:bg-rose-50/40 disabled:text-rose-300"
                title="Reject"
              >
                <XCircle size={14} />
                Reject
              </button>
              <button
                onClick={() => handleEsignInit(row.original)}
                disabled={!isApprovedOrRejected}
                className="inline-flex items-center gap-1.5 rounded-lg border border-violet-200 bg-violet-50 px-3 py-2 text-[11px] font-semibold text-violet-700 transition-all hover:bg-violet-100 disabled:cursor-not-allowed disabled:border-violet-100 disabled:bg-violet-50/40 disabled:text-violet-300"
                title="Esign"
              >
                <Zap size={14} />
                E-sign
              </button>
              <button
                onClick={() => downloadPdf(row.original)}
                disabled={!isEsignCompleted}
                className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-2 text-[11px] font-semibold text-white shadow-sm transition-all hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400 disabled:shadow-none disabled:hover:bg-slate-200"
                title={isEsignCompleted ? "Download Agreement" : "E-sign must be completed before downloading the agreement"}
              >
                <Download size={14} />
                Download Agreement
              </button>
            </div>
          );
        },
      },
    ],
    [viewedAgreements],
  );

  return (
    <div className="w-full max-w-screen-xl mx-auto p-4 space-y-6">
      <ToastContainer position="top-right" autoClose={2000} hideProgressBar theme="colored" />

      {/* Header */}
      <div className="space-y-4">
        <h1 className="text-xl font-bold text-gray-800 tracking-tight">Distributor Listing</h1>

        <div className="relative w-full max-w-2xl">
          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
            <Search className="h-4 w-4 text-indigo-400" />
          </div>
          <input
            type="text"
            placeholder="Search distributor, fund or class..."
            value={globalSearch}
            onChange={(e) => setGlobalSearch(e.target.value)}
            className="block w-full pl-11 pr-4 py-3.5 border border-slate-200 rounded-2xl bg-white text-sm text-slate-700 placeholder:text-slate-400 shadow-sm transition-all focus:outline-none focus:ring-4 focus:ring-indigo-50 focus:border-indigo-200"
          />
        </div>
      </div>

      {/* Table Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-1">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 gap-4 text-gray-400">
              <Loader2 size={40} className="animate-spin text-gray-300" />
              <span className="text-sm">Loading records...</span>
            </div>
          ) : (
            <div className="overflow-x-auto custom-registry-table">
              <style>{`
                .custom-registry-table th {
                  color: #4f46e5;
                  font-size: 11px;
                  font-weight: 800;
                  text-transform: uppercase;
                  letter-spacing: 0.05em;
                  padding: 16px 12px;
                  border-bottom: 1px solid #f3f4f6;
                }
                .custom-registry-table td {
                  padding: 16px 12px;
                  vertical-align: middle;
                }
              `}</style>
              <ReusableTable
                columns={columns}
                data={data.filter(d =>
                  (d.distributor_name || "").toLowerCase().includes(globalSearch.toLowerCase()) ||
                  (d.arn_code || "").toLowerCase().includes(globalSearch.toLowerCase())
                )}
              />
            </div>
          )}
        </div>
      </div>

      {/* Rejection Reason Modal */}
      {isRejectModalOpen && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black bg-opacity-60">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-bold text-gray-800 mb-4">
              Rejection Reason
            </h3>
            <textarea
              className="w-full border rounded p-2 focus:outline-none focus:ring-2 focus:ring-red-500"
              rows="4"
              placeholder="Enter reason for rejection..."
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
            ></textarea>
            <div className="flex justify-end gap-3 mt-4">
              <Button
                variant="cancel"
                onClick={() => setIsRejectModalOpen(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleRejectSubmit}
                className="bg-red-600 text-white hover:bg-red-700"
              >
                Submit Rejection
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Approval Confirmation Modal */}
      {isApproveModalOpen && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black bg-opacity-60">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm p-6 flex flex-col items-center">
            <div className="rounded-full bg-green-100 p-3 mb-4">
              <svg
                className="w-8 h-8 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M5 13l4 4L19 7"
                ></path>
              </svg>
            </div>
            <h3 className="text-lg font-bold text-gray-800 mb-2">
              Confirm Approval
            </h3>
            <p className="text-gray-600 text-center mb-6">
              Are you sure you want to approve{" "}
              <b>{selectedDistributorForApproval?.distributor_name}</b>?
            </p>
            <div className="flex justify-center gap-3 w-full">
              <Button
                variant="cancel"
                onClick={() => setIsApproveModalOpen(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={confirmApprove}
                className="bg-green-600 text-white hover:bg-green-700 flex-1"
              >
                Confirm
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Ops E-sign Modal */}
      <CustomDialog
        visible={isEsignModalOpen}
        onHide={() => setIsEsignModalOpen(false)}
        header={`E-sign for ${selectedDistributorForEsign?.distributor_name}`}
        width="50vw"
      >
        <div className="space-y-4">
          <div className="space-y-4">
            {esignStep === "init" ? (
              <>
                <input
                  type="text"
                  value={esignName}
                  onChange={(e) => setEsignName(e.target.value)}
                  placeholder="In presence of"
                  className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-purple-500"
                />

                <div className="flex justify-end gap-3">
                  <Button
                    variant="cancel"
                    onClick={() => setIsEsignModalOpen(false)}
                    disabled={esignLoading}
                  >
                    Cancel
                  </Button>

                  <Button
                    variant="default"
                    onClick={handleEsignSubmit}
                    disabled={esignLoading || !esignName.trim()}
                  >
                    {esignLoading ? "Submitting..." : "Submit E-sign"}
                  </Button>
                </div>
              </>
            ) : (
              <div className="text-center space-y-4">
                <div className="p-4 bg-yellow-50 text-yellow-800 rounded">
                  <p>Checking status...</p>
                  <p className="text-sm mt-1">
                    The e-sign page has been opened in a new tab. Please
                    complete the signing process there.
                  </p>
                </div>
                <p className="text-gray-600">
                  Once you have successfully signed the document, click the
                  button below to update the status.
                </p>

                <div className="flex justify-end gap-3 pt-2">
                  <Button
                    variant="outline"
                    onClick={() => setIsEsignModalOpen(false)}
                    disabled={esignLoading}
                  >
                    Close
                  </Button>

                  <Button
                    variant="default"
                    onClick={handleEsignVerify}
                    disabled={esignLoading}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    {esignLoading ? "Verifying..." : "Confirm Completion"}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </CustomDialog>

      {/* Commercial Details Modal */}
      <CustomDialog
        visible={commercialDialogVisible}
        onHide={() => {
          setCommercialDialogVisible(false);
          setCommercialDetails([]);
          setSelectedDistributorForCommercials(null);
        }}
        header={`Commercial Details${selectedDistributorForCommercials?.distributor_name ? ` - ${selectedDistributorForCommercials.distributor_name}` : ""}`}
        width="80vw"
      >
        <div className="space-y-4">
          {/* {selectedDistributorForCommercials && (
            <div className="grid grid-cols-1 gap-3 rounded-xl border border-gray-100 bg-gray-50 p-4 text-sm text-gray-700 md:grid-cols-3">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Distributor</p>
                <p className="font-semibold uppercase">{selectedDistributorForCommercials.distributor_name || "-"}</p>
              </div>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">PAN</p>
                <p className="font-semibold uppercase">{selectedDistributorForCommercials.pan || selectedDistributorForCommercials.pan_number || "-"}</p>
              </div>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">APRN</p>
                <p className="font-semibold uppercase">{selectedDistributorForCommercials.arn_code || "-"}</p>
              </div>
            </div>
          )} */}

          {commercialLoading ? (
            <div className="flex flex-col items-center justify-center py-16 gap-4 text-gray-400">
              <Loader2 size={28} className="animate-spin text-indigo-500" />
              <span className="text-xs font-bold tracking-widest uppercase">Loading commercials...</span>
            </div>
          ) : commercialDetails.length > 0 ? (
            <div className="overflow-x-auto">
              <ReusableTable columns={commercialColumns} data={commercialTableData} />
            </div>
          ) : (
            <div className="py-12 text-center text-sm font-medium text-gray-500">
              No commercial details found for this distributor.
            </div>
          )}
        </div>
      </CustomDialog>
    </div>
  );
};

export default DistributorListing;
